import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus, Users, Coins, Power, PowerOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { agentApi } from "@/lib/extensions-api";
import { formatNumber, formatDateTime } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { CopyableId } from "@/components/ui/CopyableId";
import { AgentAssignModal } from "./AgentAssignModal";
import { AgentLoadPointsModal } from "./AgentLoadPointsModal";

export function AgentsListPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [assignOpen, setAssignOpen] = useState(false);
  const [loadTarget, setLoadTarget] = useState<number | null>(null); // 🛠️ Controls the Load Modal

  const agents = useQuery({ queryKey: ["admin-agents"], queryFn: agentApi.admin.list });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      agentApi.admin.setStatus(id, status),
    onSuccess: (a) => {
      toast.success(`Agent status updated to ${a.status}.`);
      qc.invalidateQueries({ queryKey: ["admin-agents"] });
    },
  });

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Agents</h1>
          <p className="text-sm text-surface-500 mt-1">
            Assign users as Agents and load points into their allocation.
          </p>
        </div>
        <Button leftIcon={<UserPlus className="h-4 w-4" />} onClick={() => setAssignOpen(true)}>
          Assign Agent
        </Button>
      </header>

      <Card>
        <CardHeader><CardTitle>All Agents</CardTitle></CardHeader>
        <CardBody className="p-0">
          {agents.isLoading ? (
            <div className="p-5 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12" />)}
            </div>
          ) : (agents.data ?? []).length === 0 ? (
            <EmptyState icon={Users} title="No agents yet"
                          description="Click Assign Agent above to designate a user as an Agent." />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>User</TH>
                  <TH>Shop / Name</TH>
                  <TH>Status</TH>
                  <TH className="text-right">Available</TH>
                  <TH>Assigned</TH>
                  <TH className="text-right">Actions</TH>
                </TR>
              </THead>
              <tbody>
                {agents.data!.map((a) => (
                  <TR key={a.id} onClick={() => nav(`/admin/agents/${a.id}`)} className="cursor-pointer hover:bg-surface-50">
                    <TD><CopyableId value={a.user_id} /></TD>
                    <TD>
                      <div className="font-bold text-surface-900">{a.shop_name || "No Shop Name"}</div>
                      <div className="text-xs text-surface-500">{a.full_name}</div>
                    </TD>
                    <TD><StatusPill status={a.status} /></TD>
                    <TD className="text-right font-bold text-warning-700 font-mono text-lg">
                      {formatNumber(a.available_points)}
                    </TD>
                    <TD className="text-xs text-surface-500">{formatDateTime(a.assigned_at)}</TD>
                    <TD>
                      <div className="flex gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                        {/* 🛠️ THE LOAD POINTS BUTTON */}
                        <Button size="sm" variant="primary"
                                 leftIcon={<Coins className="h-3.5 w-3.5" />}
                                 onClick={() => setLoadTarget(a.id)}>
                          Load
                        </Button>

                        {a.status === "active" ? (
                          <Button size="sm" variant="outline"
                                   leftIcon={<PowerOff className="h-3.5 w-3.5 text-danger-600" />}
                                   onClick={() => setStatus.mutate({ id: a.id, status: "suspended" })}>
                            Suspend
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline"
                                   leftIcon={<Power className="h-3.5 w-3.5 text-success-600" />}
                                   onClick={() => setStatus.mutate({ id: a.id, status: "active" })}>
                            Activate
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <AgentAssignModal open={assignOpen} onClose={() => setAssignOpen(false)} />
      
      {/* 🛠️ THE LOAD POINTS MODAL */}
      <AgentLoadPointsModal
        agentId={loadTarget}
        onClose={() => setLoadTarget(null)}
      />
    </div>
  );
}